import { createTheme, type Theme, ThemeOptions } from '@mui/material/styles'
import { createContext } from 'react'
import logger from '../../app/middleware/log'

export const defaultThemeSource = (): ThemeOptions => ({
  palette: {
    primary: {
      contrastText: '#F1F2FF',
      main: '#303091',
    },
    secondary: {
      main: '#A0A0CC',
    },
    text: {
      primary: '#000000',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.MuiGrid-root > .MuiGrid-item': {
          paddingTop: '0px !important',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '12px 12px 9px 12px',
          marginBottom: '8px',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          marginTop: '4px',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '64px',
          marginTop: '0',
          height: '64px',
          '&.Mui-expanded': {
            minHeight: '64px',
            height: '64px',
            marginTop: '0',
            marginBottom: '3px'
          },
        },
        content: {
          '&.Mui-expanded.MuiAccordionSummary-contentGutters': {
            marginTop: '0',
            marginBottom: '0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '2px 16px 16px 16px'
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          '&.MuiStepLabel-alternativeLabel': {
            marginTop: '8px',
          },
        },
      },
    },
  },
})

export const defaultTheme = (): Theme => createTheme(defaultThemeSource())

export class ThemeContextState {
  theme: Theme = defaultTheme()
  setThemeContextState = (themeContextState: ThemeContextState): void => {
    logger.log('not initialized')
  }

  createAndSetTheme(themeSource: ThemeOptions) {
    const mergedThemeSource = {
      ...themeSource,
      components: {
        ...defaultThemeSource().components,
        ...(themeSource.components || {}),
      },
    }
    const newTheme = createTheme(mergedThemeSource)
    this.setTheme(newTheme)
    return newTheme
  }

  setTheme(theme: Theme): void {
    theme.typography.fontFamily = 'Roboto, sans-serif'
    this.theme = theme
    const newThemeContextState = createThemeContextState(this.theme, this.setThemeContextState)
    this.setThemeContextState(newThemeContextState)
  }
}

export const createThemeContextState = (
  theme: Theme | null = null,
  setThemeContextState: ((themeContextState: ThemeContextState) => void) | null = null
): ThemeContextState => {
  const newThemeContextState = new ThemeContextState()

  if (theme !== null) {
    theme.typography.fontFamily = 'Roboto, sans-serif'
    newThemeContextState.theme = theme
  }
  if (setThemeContextState !== null) {
    newThemeContextState.setThemeContextState = setThemeContextState
  }
  return newThemeContextState
}

export const ThemeContext = createContext<ThemeContextState>(createThemeContextState())
